import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useState,
} from 'react';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { usePopover, Popover, PopoverScreen, HideReason } from '@trello/nachos';
import { Feature } from 'app/scripts/debug/constants';
import {
  Board_Prefs_PermissionLevel,
  useWorkspacesPreambleAddBoardToTeamMutation,
  useWorkspacesPreambleBoardVisibilityMutation,
  useWorkspacesPreamblePromptQuery,
} from '@trello/graphql/generated';
import { useRestrictedBoardVisibility } from './useRestrictedBoardVisibility';
import { useRestrictedGuests } from './useRestrictedGuests';
import { BoardMemberRestrictionAlert } from './BoardMemberRestrictionAlert';
import { BoardVisibilityRestrictionAlert } from './BoardVisibilityRestrictionAlert';
import { WorkspacesPreambleCreateTeam } from './WorkspacesPreambleCreateTeam';
import { WorkspacesPreambleSelectTeam } from './WorkspacesPreambleSelectTeam';
import { forNamespace } from 'app/common/lib/util/i18n';
import { logError } from 'app/scripts/debug/log-error';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { SourceType } from '@trello/atlassian-analytics/src/constants/Source';

const format = forNamespace('workspaces preamble');

const noop = () => {};

interface WorkspacesPreamblePromptProps {
  autoShow?: boolean;
  boardId: string;
  IntroScreen: React.FunctionComponent<{
    buttonCopy: string;
    onButtonClick: () => void;
  }>;
  isBoardTeamless?: boolean;
  onCreateTeamSuccess: (teamName?: string) => void;
  onHide?: (hideReason: HideReason) => void;
  onSelectTeamSuccess?: () => void;
  onShow?: () => void;
  successAnalyticDirectObj: Noun;
  successAnalyticSource: SourceType;
  TriggerButton: ForwardRefExoticComponent<
    { boardId: string; onClick: () => void } & RefAttributes<HTMLAnchorElement>
  >;
}

enum Screens {
  Intro,
  SelectTeam,
  CreateTeam,
  BoardMemberRestriction,
  BoardVisibilityRestriction,
}

export const WorkspacesPreamblePrompt: React.FunctionComponent<WorkspacesPreamblePromptProps> = ({
  boardId,
  IntroScreen,
  isBoardTeamless,
  onCreateTeamSuccess,
  onHide = noop,
  onSelectTeamSuccess = noop,
  onShow = noop,
  autoShow,
  successAnalyticDirectObj,
  successAnalyticSource,
  TriggerButton,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [
    isBoardMemberRestrictionResolved,
    setIsBoardMemberRestrictionResolved,
  ] = useState(false);
  const [
    boardVisibilityRestrictionResolution,
    setBoardVisibilityRestrictionResolution,
  ] = useState('' as Board_Prefs_PermissionLevel);

  const { data } = useWorkspacesPreamblePromptQuery({
    variables: { boardId },
  });

  const {
    hasBoardMemberRestrictions,
    boardMembersNotInTeam,
    loading: restrictedGuestsLoading,
  } = useRestrictedGuests({
    orgId: selectedTeamId,
    boardId,
    skip: data?.board?.members.length === 1,
  });

  const {
    loading: boardVisibilityRestrictionsLoading,
    teamRestrictsCurrentBoardVisibility,
  } = useRestrictedBoardVisibility({
    orgId: selectedTeamId,
    boardId,
  });

  const [addBoardToTeam] = useWorkspacesPreambleAddBoardToTeamMutation();
  const [
    updateBoardVisibility,
  ] = useWorkspacesPreambleBoardVisibilityMutation();

  const { popoverProps, push, show, toggle, triggerRef, hide } = usePopover<
    HTMLAnchorElement
  >({
    initialScreen: Screens.Intro,
    onHide,
    onShow,
  });

  useEffect(() => {
    // Reset resolutions when the team is changed or the popover is closed
    setIsBoardMemberRestrictionResolved(false);
    setBoardVisibilityRestrictionResolution('' as Board_Prefs_PermissionLevel);
  }, [
    popoverProps.isVisible,
    selectedTeamId,
    setIsBoardMemberRestrictionResolved,
    setBoardVisibilityRestrictionResolution,
  ]);

  const onSelectTeamSubmit = async (options?: {
    boardMemberRestrictionResolved?: boolean;
    boardVisibilityRestrictionResolution?: Board_Prefs_PermissionLevel;
  }) => {
    /**
     * If adding the board to the team will result in removing board members, stop
     * and notify the user. If not, continue add the board to the team. The
     * BoardMemberRestriction popover screen will notify the user and allow them to
     * continue or return to the team selection step.
     */
    if (
      hasBoardMemberRestrictions &&
      boardMembersNotInTeam.length &&
      !options?.boardMemberRestrictionResolved &&
      !isBoardMemberRestrictionResolved
    ) {
      push(Screens.BoardMemberRestriction);
      return;
    }

    /**
     * If the board's visibility is restricted by the team, stop and notify the user.
     * The BoardVisibilityRestriction popover screen will notify the user and allow
     * them to choose another visibility for the board or return to the team selection
     * step.
     */
    if (
      teamRestrictsCurrentBoardVisibility &&
      !options?.boardVisibilityRestrictionResolution &&
      !boardVisibilityRestrictionResolution
    ) {
      push(Screens.BoardVisibilityRestriction);
      return;
    }

    try {
      /**
       * If we need to set a new visibility, we'll need to do that while moving
       * to the team. This is the only way Team Visible can be chosen as an option.
       * Otherwise, we can simply move the board to the team.
       */
      if (
        options?.boardVisibilityRestrictionResolution ||
        boardVisibilityRestrictionResolution
      ) {
        const newVisibility =
          options?.boardVisibilityRestrictionResolution ||
          boardVisibilityRestrictionResolution;
        await updateBoardVisibility({
          variables: {
            boardId,
            visibility: newVisibility,
            orgId: selectedTeamId,
          },
        });
      } else {
        await addBoardToTeam({
          variables: { boardId, orgId: selectedTeamId },
        });
      }

      Analytics.sendTrackEvent({
        action: 'updated',
        actionSubject: 'organization',
        source: successAnalyticSource,
        containers: {
          board: {
            id: boardId,
          },
          organization: {
            id: selectedTeamId,
          },
        },
        attributes: {
          newOrg: false,
          updatedOn: 'board',
        },
      });
      trackUe({
        category: Noun.BOARD_VIEW,
        verb: Verb.SUBMITS,
        directObj: successAnalyticDirectObj,
        context: {
          boardId,
          newOrg: false,
          orgId: selectedTeamId,
        },
      });

      onSelectTeamSuccess();
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.WorkspacesPreamble,
        },
      });
    }
  };

  useEffect(() => {
    if (autoShow) {
      show();
    }
  }, [autoShow, show]);

  useEffect(() => {
    // Explicit boolean equality here since isBoardTeamless may be undefined
    if (isBoardTeamless === false) {
      hide();
    }
  }, [isBoardTeamless, hide]);

  const isMemberOfTeam = !!data?.member?.idOrganizations?.length || false;
  const secondScreen = isMemberOfTeam ? Screens.SelectTeam : Screens.CreateTeam;

  // If the board and user do not meet the requirements for the WorkspacesPreamble feature, show the classic Personal button
  return (
    <>
      <TriggerButton boardId={boardId} onClick={toggle} ref={triggerRef} />
      <Popover
        {...popoverProps}
        title={
          isMemberOfTeam
            ? format('popover-title-add-to-team')
            : format('popover-title-create-team')
        }
      >
        <PopoverScreen id={Screens.Intro}>
          <IntroScreen
            onButtonClick={() => push(secondScreen)}
            buttonCopy={
              isMemberOfTeam
                ? format('prompt-continue-button-add-to-team')
                : format('prompt-continue-button-create-team')
            }
          />
        </PopoverScreen>
        <PopoverScreen id={Screens.SelectTeam}>
          <WorkspacesPreambleSelectTeam
            boardId={boardId}
            isLoadingSelectedTeam={
              restrictedGuestsLoading || boardVisibilityRestrictionsLoading
            }
            onSelectTeam={setSelectedTeamId}
            onSelectCreateTeam={() => push(Screens.CreateTeam)}
            onSubmit={onSelectTeamSubmit}
            selectedTeamId={selectedTeamId}
          />
        </PopoverScreen>
        <PopoverScreen id={Screens.CreateTeam}>
          <WorkspacesPreambleCreateTeam
            analyticsDirectObj={successAnalyticDirectObj}
            analyticsSource={successAnalyticSource}
            boardId={boardId}
            onSuccess={onCreateTeamSuccess}
          />
        </PopoverScreen>
        <PopoverScreen id={Screens.BoardMemberRestriction}>
          <BoardMemberRestrictionAlert
            boardId={boardId}
            onCancel={() => push(Screens.SelectTeam)}
            onSubmit={() => {
              setIsBoardMemberRestrictionResolved(true);
              onSelectTeamSubmit({ boardMemberRestrictionResolved: true });
            }}
            orgId={selectedTeamId}
          />
        </PopoverScreen>
        <PopoverScreen id={Screens.BoardVisibilityRestriction}>
          <BoardVisibilityRestrictionAlert
            boardId={boardId}
            onSubmit={(visibility: Board_Prefs_PermissionLevel) => {
              setBoardVisibilityRestrictionResolution(visibility);
              onSelectTeamSubmit({
                boardVisibilityRestrictionResolution: visibility,
              });
            }}
            orgId={selectedTeamId}
          />
        </PopoverScreen>
      </Popover>
    </>
  );
};
